import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';
import {GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut} from 'firebase/auth';
import {auth} from '../firebase';
import './SubscribePage.css';

const SERVER_URL = 'https://mail-attach-430209.ew.r.appspot.com';
const SUPPORT_EMAIL = 'dan@xdrxlabs.com';
const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/mail-attach-missing-attac/oncbebpmgkclejkfcolooflnniokmmka';

const plans = [
    {id: 'solo', name: 'Personal', price: 19.99, maxEmails: 1, description: 'For one inbox'},
    {id: 'plus', name: 'Multi', price: 29.99, maxEmails: 3, description: 'For all your inboxes', popular: true},
    {id: 'team', name: 'Team', price: 39.99, maxEmails: 10, description: 'For a small team'},
];

const normalizeEmail = (value) => value.trim().toLowerCase();
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

function SubscribePage() {
    const emailFromExtension = useMemo(
        () => normalizeEmail(new URLSearchParams(window.location.search).get('email') || ''),
        []
    );
    const [user, setUser] = useState(undefined);
    const visitId = useRef(null);
    useEffect(() => {
        if (!user) return;
        if (!visitId.current) visitId.current = crypto.randomUUID();
        user.getIdToken().then((token) => fetch(`${SERVER_URL}/analytics/dashboard-visit`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
            body: JSON.stringify({visitId: visitId.current}),
        })).catch(() => {});
    }, [user]);
    const [subscription, setSubscription] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState('solo');
    const [trialInfo, setTrialInfo] = useState(null);
    const [stripeEnabled, setStripeEnabled] = useState(false);
    const [stripeMode, setStripeMode] = useState('unconfigured');
    const [protectedEmails, setProtectedEmails] = useState(emailFromExtension ? [emailFromExtension] : []);
    const [emailInput, setEmailInput] = useState('');
    const [pageStatus, setPageStatus] = useState('checking');
    const [message, setMessage] = useState('');
    const [notice, setNotice] = useState('');
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [supportCopied, setSupportCopied] = useState(false);
    const [feedbackReward, setFeedbackReward] = useState(null);
    const [feedbackSaving, setFeedbackSaving] = useState(false);

    const selectedPlanDetails = plans.find((plan) => plan.id === selectedPlan);
    const ownerEmail = normalizeEmail(user?.email || '');
    const isActive = Boolean(subscription);
    const isTrial = subscription?.status === 'trialing';
    const isStripeSubscription = subscription?.provider === 'stripe';
    const isFeedbackReward = subscription?.provider === 'feedback_reward';
    const isSubscribed = isActive;
    const trialAvailable = trialInfo?.status === 'available' && !isActive;
    const cancellationScheduled = subscription?.cancelAtPeriodEnd === true;
    const currentPlanDetails = plans.find((plan) => plan.id === subscription?.plan) || null;
    const currentPlanIndex = plans.findIndex((plan) => plan.id === subscription?.plan);
    const selectedPlanIndex = plans.findIndex((plan) => plan.id === selectedPlan);
    const scheduledPlanDetails = plans.find((plan) => plan.id === subscription?.scheduledChange?.plan) || null;
    const protectedEmailsChanged = isActive && JSON.stringify([...protectedEmails].sort())
        !== JSON.stringify([...(subscription?.coveredEmails || [])].sort());

    const getCheckoutAction = () => {
        if (pageStatus === 'saving') return 'Updating…';
        if (!isActive) return stripeEnabled
            ? trialAvailable ? 'Start 3-day free trial' : 'Continue to checkout'
            : 'Activate test subscription';
        if (isFeedbackReward) return `Subscribe to ${selectedPlanDetails.name}`;
        if (isTrial && selectedPlan !== subscription?.plan) return `Switch to ${selectedPlanDetails.name}`;
        if (selectedPlanIndex > currentPlanIndex) return `Upgrade to ${selectedPlanDetails.name}`;
        if (selectedPlanIndex < currentPlanIndex) return `Downgrade to ${selectedPlanDetails.name}`;
        if (subscription?.scheduledChange) return 'Cancel scheduled downgrade';
        if (protectedEmailsChanged) return 'Update protected emails';
        return 'Current plan';
    };

    const copySupportEmail = async () => {
        try {
            await navigator.clipboard.writeText(SUPPORT_EMAIL);
        } catch (error) {
            const textArea = document.createElement('textarea');
            textArea.value = SUPPORT_EMAIL;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            textArea.remove();
        }

        setSupportCopied(true);
        window.setTimeout(() => setSupportCopied(false), 2200);
    };

    useEffect(() => onAuthStateChanged(auth, setUser), []);

    useEffect(() => {
        fetch(`${SERVER_URL}/billing/config`)
            .then((response) => response.json())
            .then((data) => {
                setStripeEnabled(data.stripeEnabled === true);
                setStripeMode(data.stripeMode || 'unconfigured');
            })
            .catch(() => {
                setStripeEnabled(false);
                setStripeMode('unconfigured');
            });
    }, []);

    useEffect(() => {
        if (user === undefined) return;
        if (!user) {
            setPageStatus('idle');
            return;
        }

        let cancelled = false;
        setPageStatus('checking');
        setMessage('');

        user.getIdToken()
            .then((token) => fetch(`${SERVER_URL}/billing/account`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({suggestedEmail: emailFromExtension}),
            }))
            .then(async (response) => {
                const data = await response.json();
                if (!response.ok) throw new Error(data.error || 'Could not load your account.');
                if (cancelled) return;
                setTrialInfo(data.trial || null);
                setFeedbackReward(data.feedbackReward || null);

                if (data.isActive && data.subscription) {
                    setSubscription(data.subscription);
                    setSelectedPlan(data.subscription.plan);
                    setProtectedEmails(data.subscription.coveredEmails || []);
                    if (
                        emailFromExtension
                        && !(data.subscription.coveredEmails || []).includes(emailFromExtension)
                        && (data.subscription.coveredEmails || []).length < data.subscription.maxEmails
                    ) {
                        setEmailInput(emailFromExtension);
                    }
                } else {
                    setSubscription(null);
                    setSelectedPlan('solo');
                    setProtectedEmails(emailFromExtension ? [emailFromExtension] : []);
                }
                setPageStatus('idle');
            })
            .catch((error) => {
                if (!cancelled) {
                    setMessage(error.message);
                    setPageStatus('idle');
                }
            });

        return () => { cancelled = true; };
    }, [user, emailFromExtension]);

    const authenticatedRequest = async (url, body = {}) => {
        const token = await user.getIdToken();
        const response = await fetch(`${SERVER_URL}${url}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'The request could not be completed.');
        return data;
    };

    const loginWithGoogle = async () => {
        setMessage('');
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({prompt: 'select_account'});
        try {
            await signInWithPopup(auth, provider);
        } catch (error) {
            setMessage(error.message || 'Could not sign in with Google.');
        }
    };

    const selectPlan = (plan) => {
        if (protectedEmails.length > plan.maxEmails) {
            setMessage(`Remove ${protectedEmails.length - plan.maxEmails} email ${protectedEmails.length - plan.maxEmails === 1 ? 'address' : 'addresses'} before choosing this plan.`);
            return;
        }
        setMessage('');
        setNotice('');
        setSelectedPlan(plan.id);
    };

    const addEmail = () => {
        const email = normalizeEmail(emailInput);
        if (!isValidEmail(email)) {
            setMessage('Enter a valid email address.');
            return;
        }
        if (protectedEmails.includes(email)) {
            setMessage('This email is already in your plan.');
            return;
        }
        if (protectedEmails.length >= selectedPlanDetails.maxEmails) {
            setMessage(`Your ${selectedPlanDetails.name} plan covers up to ${selectedPlanDetails.maxEmails} email addresses.`);
            return;
        }
        setProtectedEmails([...protectedEmails, email]);
        setEmailInput('');
        setMessage('');
        setNotice('');
    };

    const removeEmail = (email) => {
        setProtectedEmails(protectedEmails.filter((item) => item !== email));
        setMessage('');
        setNotice('');
    };

    const saveSubscription = async () => {
        setPageStatus('saving');
        setMessage('');
        setNotice('');
        try {
            const endpoint = stripeEnabled
                ? isStripeSubscription ? '/stripe/change-plan' : '/stripe/checkout-session'
                : '/test-subscription';
            const data = await authenticatedRequest(endpoint, {
                plan: selectedPlan,
                coveredEmails: protectedEmails,
            });
            if (data.url) {
                window.location.assign(data.url);
                return;
            }
            setSubscription(data.subscription);
            setSelectedPlan(data.subscription.plan);
            setTrialInfo((current) => current ? {...current, status: 'expired'} : current);
            setProtectedEmails(data.subscription.coveredEmails || []);
            if (data.changeType === 'downgrade_scheduled') {
                setNotice(`Downgrade scheduled for ${formatDate(data.subscription.scheduledChange?.effectiveAt)}. Your current plan stays active until then.`);
            } else if (data.changeType === 'upgrade_immediate') {
                setNotice(data.billingAdjustment
                    ? `Upgrade active now. Prorated difference: $${Number(data.billingAdjustment.amountUsd || 0).toFixed(2)} USD in test mode.`
                    : 'Upgrade active now. Stripe charged the prorated difference.');
            } else if (data.changeType === 'scheduled_change_canceled') {
                setNotice('Scheduled downgrade canceled. Your current plan will renew normally.');
            } else if (data.changeType === 'trial_plan_changed') {
                setNotice(`Trial updated to ${selectedPlanDetails.name}. You will be charged $${selectedPlanDetails.price}/year plus applicable VAT when the trial ends.`);
            } else {
                setNotice('Your subscription has been updated.');
            }
            setPageStatus('idle');
        } catch (error) {
            setMessage(error.message);
            setPageStatus('idle');
        }
    };

    const cancelSubscription = async () => {
        setPageStatus('saving');
        setMessage('');
        setNotice('');
        try {
            const data = await authenticatedRequest(isStripeSubscription ? '/stripe/cancel' : '/test-subscription/cancel');
            setSubscription(data.subscription);
            setShowCancelConfirm(false);
            setNotice(`Cancellation scheduled. MailAttach stays active until ${formatDate(data.subscription.cancellationEffectiveAt)}.`);
            setPageStatus('idle');
        } catch (error) {
            setMessage(error.message);
            setPageStatus('idle');
        }
    };

    const resumeSubscription = async () => {
        setPageStatus('saving');
        setMessage('');
        setNotice('');
        try {
            const data = await authenticatedRequest(isStripeSubscription ? '/stripe/resume' : '/test-subscription/resume');
            setSubscription(data.subscription);
            setNotice('Cancellation removed. Your subscription will renew normally.');
            setPageStatus('idle');
        } catch (error) {
            setMessage(error.message);
            setPageStatus('idle');
        }
    };

    const openBillingPortal = async () => {
        setPageStatus('saving');
        setMessage('');
        try {
            const data = await authenticatedRequest('/stripe/portal-session');
            window.location.assign(data.url);
        } catch (error) {
            setMessage(error.message);
            setPageStatus('idle');
        }
    };

    const startFreeMonth = async () => {
        window.open(CHROME_STORE_URL, '_blank', 'noopener,noreferrer');
        setFeedbackSaving(true);
        setMessage('');
        setNotice('');
        authenticatedRequest('/feedback/review-event', {action: 'opened'}).catch(() => {});
        try {
            const data = await authenticatedRequest('/feedback/reward', {
                protectedEmail: protectedEmails[0] || emailFromExtension || ownerEmail,
            });
            setSubscription(data.subscription);
            setSelectedPlan(data.subscription.plan);
            setProtectedEmails(data.subscription.coveredEmails || [ownerEmail]);
            setFeedbackReward(data.feedbackReward);
            setNotice(`Your free month is active until ${formatDate(data.feedbackReward.endsAt)}.`);
        } catch (error) {
            setMessage(error.message);
        } finally {
            setFeedbackSaving(false);
        }
    };

    const formatDate = (value) => value
        ? new Intl.DateTimeFormat('en', {dateStyle: 'medium'}).format(new Date(value))
        : '—';

    if (user === undefined || (user && pageStatus === 'checking')) {
        return (
            <div className="billing-page">
                <div className="billing-loader"><span></span>Loading your MailAttach account…</div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="billing-page auth-layout">
                <Helmet><title>MailAttach account</title></Helmet>
                <section className="auth-panel">
                    <span className="section-kicker">MAILATTACH</span>
                    <h1>Sign in to MailAttach</h1>
                    <p>Manage the Gmail addresses you want to protect.</p>
                    {emailFromExtension && (
                        <div className="detected-email">
                            <span>Detected in Gmail</span>
                            <strong>{emailFromExtension}</strong>
                        </div>
                    )}
                    {message && <div className="inline-error">{message}</div>}
                    <button className="google-button" type="button" onClick={loginWithGoogle}>
                        <svg className="google-logo" viewBox="0 0 18 18" aria-hidden="true">
                            <path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844c-.209 1.125-.843 2.079-1.797 2.716v2.258h2.909c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.258c-.806.54-1.835.859-3.047.859-2.344 0-4.328-1.584-5.037-3.712H.956v2.333A9 9 0 0 0 9 18z"/>
                            <path fill="#FBBC05" d="M3.963 10.709A5.41 5.41 0 0 1 3.681 9c0-.593.102-1.17.282-1.709V4.958H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.042l3.007-2.333z"/>
                            <path fill="#EA4335" d="M9 3.579c1.321 0 2.507.454 3.441 1.345l2.581-2.581C13.463.891 11.426 0 9 0A9 9 0 0 0 .956 4.958l3.007 2.333C4.672 5.163 6.656 3.579 9 3.579z"/>
                        </svg>
                        Sign in with Google
                    </button>
                </section>
            </div>
        );
    }

    return (
        <div className="billing-page">
            <Helmet><title>{isActive ? 'Manage subscription' : 'Choose a plan'} - MailAttach</title></Helmet>
            <main className="billing-shell">
                <header className="billing-topbar">
                    <div className="mini-brand"><span>M</span> MailAttach</div>
                    <div className="owner-menu">
                        <div><span>Account</span><strong>{ownerEmail}</strong></div>
                        <button className="signout-button" type="button" onClick={() => signOut(auth)}>
                            Sign out <span>→</span>
                        </button>
                    </div>
                </header>

                <section className="how-it-works" aria-labelledby="how-it-works-title">
                    <div className="how-it-works-heading">
                        <div>
                            <span className="section-kicker">HOW DOES IT WORK?</span>
                            <h2 id="how-it-works-title">Mail normally. We watch the attachments.</h2>
                        </div>
                        <span>Works directly in Gmail</span>
                    </div>

                    <div className="how-it-works-flow">
                        <article className="how-step">
                            <div className="how-step-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24">
                                    <path d="M4 5.5h16v13H4z"/>
                                    <path d="m4 7 8 6 8-6"/>
                                </svg>
                            </div>
                            <div>
                                <span>01</span>
                                <h3>Write your email</h3>
                                <p>Compose and send as usual.</p>
                            </div>
                        </article>

                        <span className="how-flow-arrow" aria-hidden="true">→</span>

                        <article className="how-step">
                            <div className="how-step-icon check-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24">
                                    <path d="M12 3 4.5 6v5.2c0 4.7 3.2 8.1 7.5 9.8 4.3-1.7 7.5-5.1 7.5-9.8V6z"/>
                                    <path d="m8.5 12 2.2 2.2 4.8-5"/>
                                </svg>
                            </div>
                            <div>
                                <span>02</span>
                                <h3>MailAttach checks</h3>
                                <p>We detect when a file may be missing.</p>
                            </div>
                        </article>

                        <span className="how-flow-arrow" aria-hidden="true">→</span>

                        <article className="how-step">
                            <div className="how-step-icon alert-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24">
                                    <path d="M12 3 2.8 19h18.4z"/>
                                    <path d="M12 9v4.5M12 17h.01"/>
                                </svg>
                            </div>
                            <div>
                                <span>03</span>
                                <h3>Get one clear alert</h3>
                                <p>Attach the file—or send anyway.</p>
                            </div>
                        </article>
                    </div>
                    <button className={`dashboard-support ${supportCopied ? 'copied' : ''}`} type="button" onClick={copySupportEmail}>
                        <span>Questions?</span>
                        <strong>{SUPPORT_EMAIL}</strong>
                        <i aria-live="polite">{supportCopied ? 'Copied!' : 'Copy email'}</i>
                    </button>
                </section>

                {(!feedbackReward?.status || feedbackReward.status === 'available') && (
                    <section className="feedback-hub" aria-label="Chrome Web Store review reward">
                        <div className="feedback-reward-card">
                            <span className="feedback-eyebrow">CHROME WEB STORE</span>
                            <div>
                                <h2>Leave us a review. Get one month on us.</h2>
                            </div>
                            <button type="button" onClick={startFreeMonth} disabled={feedbackSaving}>
                                {feedbackSaving ? 'Starting…' : 'Leave a review'}
                            </button>
                        </div>
                    </section>
                )}

                <section className="billing-heading">
                    <div>
                        <span className="section-kicker">MAILATTACH</span>
                        <h1>Your annual plan</h1>
                    </div>
                    <div className={`protection-status ${cancellationScheduled ? 'ending' : isTrial ? 'trial' : isActive ? 'active' : 'inactive'}`}>
                        <div>
                            <div className="status-badges">
                                <span className="status-pill">{isFeedbackReward ? 'Free access' : isSubscribed ? 'Subscribed' : 'Not subscribed'}</span>
                                {cancellationScheduled && <span className="status-pill ending-pill">Cancellation scheduled</span>}
                            </div>
                            <strong>{isSubscribed ? currentPlanDetails?.name || 'Annual plan' : 'No plan selected'}</strong>
                            <span className="status-phase">{isTrial
                                ? cancellationScheduled
                                    ? `Free trial · access ends ${formatDate(subscription.cancellationEffectiveAt || subscription.currentPeriodEnd)}`
                                    : `Free trial · billing starts ${formatDate(subscription.currentPeriodEnd)}`
                                : isFeedbackReward
                                    ? `Feedback month · ends ${formatDate(subscription.currentPeriodEnd)}`
                                    : isSubscribed
                                    ? `Paid period · renews ${formatDate(subscription.currentPeriodEnd)}`
                                    : trialAvailable ? '3-day trial available' : 'Choose an annual plan below'}</span>
                        </div>
                    </div>
                </section>

                {cancellationScheduled && (
                    <section className="scheduled-change cancellation-change" aria-label="Scheduled cancellation">
                        <div>
                            <span>Cancellation scheduled</span>
                        </div>
                        <p>Your plan and protected emails stay active until {formatDate(subscription.cancellationEffectiveAt)}. It will not renew.</p>
                    </section>
                )}

                {scheduledPlanDetails && (
                    <section className="scheduled-change" aria-label="Scheduled plan change">
                        <div>
                            <span>Scheduled downgrade</span>
                            <strong>{currentPlanDetails?.name} → {scheduledPlanDetails.name}</strong>
                        </div>
                        <p>Your current plan stays active until {formatDate(subscription.scheduledChange.effectiveAt)}. No refund is issued.</p>
                    </section>
                )}

                <div className="dashboard-grid">
                    <section className="plans-column" aria-label="Subscription plans">
                        <div className="column-heading">
                            <h2>Plans</h2>
                            <span>3-day free trial · annual billing</span>
                        </div>
                        <div className="plan-section">
                            {plans.map((plan) => {
                                const isCurrentPlanCard = !isFeedbackReward && subscription?.plan === plan.id;
                                return (
                                    <button
                                        key={plan.id}
                                        type="button"
                                        className={`clean-plan-card ${selectedPlan === plan.id ? 'selected' : ''} ${isCurrentPlanCard ? 'current' : ''} ${plan.popular ? 'popular-plan' : ''}`}
                                        onClick={() => selectPlan(plan)}
                                    >
                                        {isCurrentPlanCard
                                            ? <span className="popular-label current-label">{isTrial ? 'Trial active' : 'Current plan'}</span>
                                            : plan.popular && <span className="popular-label">Most popular</span>}
                                        <span className="plan-title">{plan.name}</span>
                                        <span className="plan-description">{plan.description}</span>
                                        <span className={`plan-trial-note ${trialAvailable ? 'available' : ''}`}>
                                            {trialAvailable ? '3 days free' : 'Annual subscription'}
                                        </span>
                                        <span className="plan-email-count">
                                            <strong>{plan.maxEmails}</strong>
                                            <span>{plan.maxEmails === 1 ? 'email address' : 'email addresses'}</span>
                                        </span>
                                        <span className="plan-price">
                                            <span className="plan-price-monthly">
                                                <strong>${(plan.price / 12).toFixed(2)}</strong>
                                                <small>/ month</small>
                                            </span>
                                            <small className="annual-billing">
                                                Billed ${plan.price} yearly · excl. VAT
                                            </small>
                                        </span>
                                        <span className="selection-dot"><i></i></span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section className="email-manager">
                        <div className="email-manager-heading">
                            <div>
                                <h2>Protected emails</h2>
                                <p>Set them up after subscribing and change them anytime.</p>
                            </div>
                            <span>{protectedEmails.length} / {selectedPlanDetails.maxEmails}</span>
                        </div>

                        <div className="email-list">
                            {protectedEmails.map((email) => (
                                <div className="email-row" key={email}>
                                    <span className="email-avatar">{email.charAt(0).toUpperCase()}</span>
                                    <strong>{email}</strong>
                                    <span className="protected-label">Protected</span>
                                    <button type="button" aria-label={`Remove ${email}`} onClick={() => removeEmail(email)}>×</button>
                                </div>
                            ))}
                            {!protectedEmails.length && <div className="empty-emails">No protected emails yet. Add them whenever you're ready.</div>}
                        </div>

                        {protectedEmails.length < selectedPlanDetails.maxEmails && (
                            <div className="add-email-row">
                                <input
                                    type="email"
                                    value={emailInput}
                                    onChange={(event) => setEmailInput(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            addEmail();
                                        }
                                    }}
                                    placeholder="another@gmail.com"
                                />
                                <button type="button" onClick={addEmail}>Add email</button>
                            </div>
                        )}
                    </section>
                </div>

                {message && <div className="inline-error">{message}</div>}
                {notice && <div className="inline-notice">{notice}</div>}

                <section className="checkout-bar">
                    <div>
                        <span>{selectedPlanDetails.name} plan</span>
                        <strong>${selectedPlanDetails.price} <small>USD / year · excl. VAT</small></strong>
                        {trialAvailable && <small>3 days free. Card required. Then ${selectedPlanDetails.price}/year + applicable VAT.</small>}
                        {isTrial && <small>Trial ends {formatDate(subscription.currentPeriodEnd)}. You’ll be charged automatically unless you cancel.</small>}
                        {!trialAvailable && !isTrial && <small>Applicable VAT is calculated at checkout.</small>}
                        {!isTrial && isActive && !isFeedbackReward && <small>Renews {formatDate(subscription.currentPeriodEnd)}</small>}
                    </div>
                    <button
                        className="checkout-button"
                        type="button"
                        onClick={saveSubscription}
                        disabled={pageStatus === 'saving' || (isStripeSubscription && isActive && selectedPlan === subscription?.plan && !protectedEmailsChanged && !subscription?.scheduledChange)}
                    >
                        {getCheckoutAction()}
                    </button>
                </section>

                <div className="billing-footer-actions">
                    <span>{cancellationScheduled
                        ? `Active until ${formatDate(subscription.cancellationEffectiveAt)}`
                        : isTrial
                            ? `3 days free · then $${currentPlanDetails?.price}/year + applicable VAT`
                            : trialAvailable
                                ? 'No charge today · cancel before the trial ends'
                                : stripeEnabled
                                    ? stripeMode === 'live' ? 'Secure billing by Stripe' : 'Stripe test mode'
                                    : 'Payments unavailable'}</span>
                    <div className="billing-action-buttons">
                        {isStripeSubscription && (
                            <button className="manage-billing-button" type="button" onClick={openBillingPortal} disabled={pageStatus === 'saving'}>
                                Manage billing
                            </button>
                        )}
                        {isActive && !isFeedbackReward && (
                            <button
                                className={cancellationScheduled ? 'keep-subscription-button' : 'cancel-subscription-button'}
                                type="button"
                                onClick={cancellationScheduled ? resumeSubscription : () => setShowCancelConfirm(true)}
                                disabled={pageStatus === 'saving'}
                            >
                                {cancellationScheduled ? 'Keep subscription' : 'Cancel subscription'}
                            </button>
                        )}
                    </div>
                </div>

                {showCancelConfirm && (
                    <div className="cancel-dialog-backdrop" role="presentation" onClick={() => setShowCancelConfirm(false)}>
                        <div className="cancel-dialog" role="dialog" aria-modal="true" aria-labelledby="cancel-title" onClick={(event) => event.stopPropagation()}>
                            <span className="cancel-icon">!</span>
                            <h2 id="cancel-title">Cancel subscription?</h2>
                            <p>{isTrial
                                ? `Your free trial stays active until ${formatDate(subscription.currentPeriodEnd)}. After that, MailAttach turns OFF and you will not be charged.`
                                : `MailAttach will remain active until ${formatDate(subscription.currentPeriodEnd)}, then turn OFF. There is no refund for the current period.`}</p>
                            <div>
                                <button className="keep-plan-button" type="button" onClick={() => setShowCancelConfirm(false)}>Keep it active</button>
                                <button className="confirm-cancel-button" type="button" onClick={cancelSubscription} disabled={pageStatus === 'saving'}>
                                    {pageStatus === 'saving' ? 'Scheduling…' : 'Cancel subscription'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default SubscribePage;
